import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import './carousel-base.css';
import './carousel.css';

const getPresetConfig = (preset) => {
  switch (preset) {
    case 'loop':
      return { loop: true };
    case 'scroll':
      return { slidesToScroll: 1 };
    case 'contain':
      return { containScroll: 'trimSnaps' };
    case 'dragFree':
      return { dragFree: true };
    case 'snaps':
      return { skipSnaps: false };
    default:
      return {};
  }
};

// Helper to generate color class name from backgroundColor value
const getColorClassName = (color) => {
  if (!color) return '';
  // Convert var(--name) to color-var-name or #HEXCODE to color-hexcode
  return color
    .replace(/var\(--([^)]+)\)/, 'color-var-$1')
    .replace(/^#/, 'color-')
    .toLowerCase();
};

const EmblaCarousel = ({ data, id }) => {
  // call all hooks first
  const dispatch = useDispatch();
  const searchResults = useSelector(
    (state) => state.search?.subrequests?.[id] || null,
  );

  // Calculate slides to show based on data and screen size
  const slidesToShow = parseInt(data?.slidesToShow, 10) || 1;
  const minSlidesOnMobile = parseInt(data?.minSlidesOnMobile, 10) || 1;
  const [effectiveSlidesToShow, setEffectiveSlidesToShow] = useState(() => {
    const isMobileInit =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px)').matches;
    return isMobileInit ? minSlidesOnMobile : slidesToShow;
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  // Setup Embla carousel configuration
  const carouselType = data?.carouselType || 'default';
  const configOverrides = {
    default: { align: 'start', loop: data?.loop ?? true },
    centered: { align: 'center', loop: true },
    peek: { align: 'start', loop: true },
    cards: { align: 'start', loop: false, containScroll: 'trimSnaps' },
  };
  const presetConfig = getPresetConfig(data?.preset);
  const baseConfig = configOverrides[carouselType] || configOverrides.default;
  const config = { ...baseConfig, ...presetConfig };
  const plugins = data?.autoplay
    ? [Autoplay({ delay: data?.autoplayDelay || 4000 })]
    : [];

  const [viewportRef, embla] = useEmblaCarousel(
    {
      ...config,
      loop: data?.loop ?? true,
      align: 'start',
      slidesToScroll: effectiveSlidesToShow,
    },
    plugins,
  );

  // Effect for responsive slide calculation
  useEffect(() => {
    const calculateSlidesToShow = () => {
      const isMobile =
        typeof window !== 'undefined' &&
        window.matchMedia('(max-width: 768px)').matches;
      setEffectiveSlidesToShow(isMobile ? minSlidesOnMobile : slidesToShow);
    };
    calculateSlidesToShow();
    window.addEventListener('resize', calculateSlidesToShow);
    return () => window.removeEventListener('resize', calculateSlidesToShow);
  }, [slidesToShow, minSlidesOnMobile]);

  // Effect to fetch listing data when using listing mode
  useEffect(() => {
    if (data?.useListing && data?.query && id) {
      const searchOptions = {
        metadata_fields: [
          'title',
          'description',
          'image_field',
          'image_scales',
          'effective',
          'start',
          'end',
          'portal_type',
        ],
      };

      // Handle query object structure for search criteria
      if (typeof data.query === 'object' && data.query !== null) {
        if (data.query.query && Array.isArray(data.query.query)) {
          // Extract sort options from query if present
          if (data.query.sort_on) {
            searchOptions.sort_on = data.query.sort_on;
          }
          if (data.query.sort_order) {
            searchOptions.sort_order = data.query.sort_order;
          }
          if (data.query.b_size) {
            searchOptions.b_size = data.query.b_size;
          }

          data.query.query.forEach((criterion) => {
            if (criterion.i === 'portal_type' && criterion.v) {
              searchOptions.portal_type = Array.isArray(criterion.v)
                ? criterion.v
                : [criterion.v];
            }
            if (criterion.i === 'path' && criterion.v) {
              searchOptions.path = criterion.v;
            }
            if (criterion.i === 'review_state' && criterion.v) {
              searchOptions.review_state = Array.isArray(criterion.v)
                ? criterion.v
                : [criterion.v];
            }
            if (criterion.i === 'Subject' && criterion.v) {
              searchOptions.Subject = Array.isArray(criterion.v)
                ? criterion.v
                : [criterion.v];
            }
            if (criterion.i === 'Creator' && criterion.v) {
              searchOptions.Creator = Array.isArray(criterion.v)
                ? criterion.v
                : [criterion.v];
            }
          });
          dispatch(searchContent('', searchOptions, id));
        } else {
          // Handle alternative query format
          const { query, sort_on, sort_order, b_size, ...otherParams } = data.query;
          Object.assign(searchOptions, otherParams);
          
          // Apply sorting from query
          if (sort_on) {
            searchOptions.sort_on = sort_on;
          }
          if (sort_order) {
            searchOptions.sort_order = sort_order;
          }
          if (b_size) {
            searchOptions.b_size = b_size;
          }
          
          if (query && Array.isArray(query)) {
            searchOptions.query = query;
          }
          dispatch(searchContent('', searchOptions, id));
        }
      } else if (typeof data.query === 'string') {
        dispatch(searchContent(data.query, searchOptions, id));
      } else {
        searchOptions.query = data.query;
        dispatch(searchContent('', searchOptions, id));
      }
    }
  }, [
    data?.useListing,
    data?.query,
    dispatch,
    id,
  ]);

  // Effect for Embla carousel setup
  useEffect(() => {
    if (!embla) return;
    embla.on('select', () => setSelectedIndex(embla.selectedScrollSnap()));
  }, [embla]);

  // Early return if id is undefined - AFTER all hooks are called
  if (!id) {
    return <div>EmblaCarousel: Missing block ID</div>;
  }

  // Function to get the best image for background from Plone search results
  const getImageUrl = (item) => {
    // Check if the item has image_scales and image_field
    if (item.image_scales && item.image_field) {
      // Construct the image URL based on Plone's structure
      return `${item['@id']}/@@images/${item.image_field}`;
    }

    // Fallback for manual slides
    if (item.preview_image) {
      return `${item.preview_image['@id']}/@@images/image`;
    }
    if (item.lead_image) {
      return `${item.lead_image['@id']}/@@images/image`;
    }
    if (item.image) {
      const image = Array.isArray(item.image) ? item.image[0] : item.image;
      return image?.['@id']
        ? `${image['@id']}/@@images/image`
        : image?.download || '';
    }

    return '';
  };

  // Function to format effective date
  const formatEffectiveDate = (dateString, isEvent, startDate) => {
    if (!dateString && !startDate) return '';
    
    const dateToFormat = isEvent && startDate ? startDate : dateString;
    const prefix = isEvent && startDate ? 'Starts ' : '';
    
    try {
      const date = new Date(dateToFormat);
      return prefix + date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      return dateToFormat;
    }
  };

  // Function to convert listing items to slide format
  const convertItemToSlide = (item) => ({
    heading: item.title || '',
    content: item.description || '',
    image: null, // We'll handle images differently for listing vs manual
    link: item,
    effectiveDate: item.effective || '',
    startDate: item.start || '',
    portalType: item['@type'] || '',
  });

  // Function to get slides either from listing or manual slides
  const getSlides = () => {
    if (data.useListing && searchResults?.items) {
      return searchResults.items.map(convertItemToSlide);
    }
    return data.slides || [];
  };

  const slides = getSlides();

  const getHref = (link) => {
    const item = Array.isArray(link) ? link[0] : link;
    if (!item || typeof item !== 'object') return '#';
    const url = item['@id'] || '';
    const isFile = item['@type'] === 'File';
    return url ? (isFile ? `${url}/@@download/file` : url) : '#';
  };

  const renderLink = (slide) => {
    const href = getHref(slide.link);
    const label = slide.buttonText?.trim();
    if (!href || !label || data.hideButtons) return null;

    const isExternal = href.startsWith('http');

    return (
      <a
        href={href}
        className="carousel-button"
        {...(isExternal
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
      >
        {label}
      </a>
    );
  };

  const handleSlideClick = (slide) => {
    if (!data.clickableSlides) return;

    const href = getHref(slide.link);
    if (!href || href === '#') return;

    const isExternal = href.startsWith('http');

    if (isExternal) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = href;
    }
  };

  const handleSlideKeyDown = (event, slide) => {
    // Handle keyboard interactions for accessibility
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSlideClick(slide);
    }
  };

  const scrollPrev = () => embla && embla.scrollPrev();
  const scrollNext = () => embla && embla.scrollNext();
  const scrollTo = (index) => embla && embla.scrollTo(index);

  const modeClass = data.mode ? `mode-${data.mode}` : '';
  const displayModeClass = data.displayMode ? `mode-${data.displayMode}` : '';
  const showArrows = data.hideArrows !== true && slides.length > 1;
  const showDots = data.hideDots !== true && slides.length > 0;
  const pageCount = Math.ceil(slides.length / effectiveSlidesToShow);

  // Build class names for tone and color
  const toneClass = data.textTone ? `tone-${data.textTone}` : '';
  const colorClass = getColorClassName(data.backgroundColor);
  const outerClassName = data.outerClassName || '';

  return (
    <div
      className={`embla ${data.isFullWidth ? 'full-width' : ''} align-${data.alignment || 'left'} ${modeClass} ${displayModeClass} type-${carouselType} ${data.equalHeight ? 'equal-height' : ''} ${toneClass} ${colorClass} ${outerClassName}`.trim()}
      style={{ backgroundColor: data.backgroundColor || 'transparent' }}
    >
      <div className="embla__inner">
        <div className="embla__above__viewport">
          {data.title && <h2 className="embla__title">{data.title}</h2>}

          {showArrows && slides.length > 1 && (
            <div className="embla__arrows">
              <button className="embla__prev" onClick={scrollPrev}>
                <i class="bi bi-arrow-left-circle"></i>
              </button>
              <button className="embla__next" onClick={scrollNext}>
                <i class="bi bi-arrow-right-circle"></i>
              </button>
            </div>
          )}
        </div>

        <div className="embla__viewport" ref={viewportRef}>
          <div className="embla__container">
            {slides.map((slide, index) => {
              const imageUrl =
                data.useListing &&
                searchResults?.items &&
                searchResults.items.length > 0
                  ? getImageUrl(slide.link)
                  : (() => {
                      const image = Array.isArray(slide.image)
                        ? slide.image[0]
                        : slide.image;
                      return image?.['@id']
                        ? `${image['@id']}/@@images/image`
                        : image?.download || '';
                    })();

              const href = getHref(slide.link);
              const isClickable = data.clickableSlides && href && href !== '#';

              // Determine slide mode classes
              const slideDisplayMode = data.displayMode || 'full';
              const slideModeClass =
                slideDisplayMode !== 'full' ? slideDisplayMode : '';

              return (
                <div
                  className="embla__slide"
                  key={index}
                  style={{ flex: `0 0 ${100 / effectiveSlidesToShow}%` }}
                >
                  {slideDisplayMode === 'image-top' ? (
                    <div
                      className={`carousel-slide-inner ${slideModeClass} ${isClickable ? 'clickable' : ''}`}
                      onClick={() => handleSlideClick(slide)}
                      onKeyDown={(event) => handleSlideKeyDown(event, slide)}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      aria-label={
                        isClickable ? `Navigate to ${slide.heading}` : undefined
                      }
                    >
                      <div className="card">
                        {imageUrl && (
                          <div className="card-media">
                            <img src={imageUrl} alt={slide.heading || ''} />
                          </div>
                        )}
                        <div className="card-content">
                          {slide.heading && <h3>{slide.heading}</h3>}
                          {!data.hideDescription && slide.content && (
                            <p>{slide.content}</p>
                          )}
                          {data.showEffectiveDate && (slide.effectiveDate || slide.startDate) && (
                            <p className="effective-date">
                              {formatEffectiveDate(
                                slide.effectiveDate,
                                slide.portalType === 'Event',
                                slide.startDate
                              )}
                            </p>
                          )}
                          {renderLink(slide)}
                        </div>
                      </div>
                    </div>
                  ) : slideDisplayMode === 'image-only' ? (
                    <div
                      className={`carousel-slide-inner ${slideModeClass} ${isClickable ? 'clickable' : ''}`}
                      onClick={() => handleSlideClick(slide)}
                      onKeyDown={(event) => handleSlideKeyDown(event, slide)}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      aria-label={
                        isClickable ? `Navigate to ${slide.heading}` : undefined
                      }
                    >
                      {imageUrl ? (
                        <img src={imageUrl} alt={slide.heading || ''} />
                      ) : (
                        <div className="image-placeholder">No image</div>
                      )}
                    </div>
                  ) : (
                    <div
                      className={`carousel-slide-inner ${isClickable ? 'clickable' : ''}`}
                      style={{
                        backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
                        cursor: isClickable ? 'pointer' : 'default',
                      }}
                      onClick={() => handleSlideClick(slide)}
                      onKeyDown={(event) => handleSlideKeyDown(event, slide)}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      aria-label={
                        isClickable ? `Navigate to ${slide.heading}` : undefined
                      }
                    >
                      {data.showEffectiveDate && (slide.effectiveDate || slide.startDate) && (
                        <p className="effective-date">
                          {formatEffectiveDate(
                            slide.effectiveDate,
                            slide.portalType === 'Event',
                            slide.startDate
                          )}
                        </p>
                      )}
                      <h5>{slide.heading}</h5>
                      {!data.hideDescription && slide.content && (
                        <p>{slide.content}</p>
                      )}
                      {renderLink(slide)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {showDots && pageCount > 1 && (
          <div className="embla__nav">
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                onClick={() => scrollTo(i)}
                className={`embla__dot ${selectedIndex === i ? 'is-selected' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmblaCarousel;